import useAxios from 'hooks/useAxios'
import { toast } from 'libs/toast'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'semantic-ui-react'

import UploadTranslationPage from './UploadTranslationPage'

import { useCsvUpload } from 'hooks/useCsvUpload'
import TranslationReviewPage from './TranslationReviewPage'
import { languageOption } from 'organisms/FeedbackFilterGroup/constants'
import { validateSurveyLanguageCsvFile } from 'libs/csv-validator'
import ImportFailedPage from './ImportFailedPage'
import { csvDownload } from 'libs/csv-download'
import { slugify } from 'libs/manual-slugify'

LanguageImportModal.propTypes = {
  modal: PropTypes.elementType,
  close: PropTypes.func,
  survey: PropTypes.object,
  workspaceToken: PropTypes.string,
  refetchSurvey: PropTypes.func,
  defaultLanguage: PropTypes.string,
  surveyLanguages: PropTypes.arrayOf(PropTypes.string),
}

export function LanguageImportModal({
  modal: Modal,
  close,
  survey,
  workspaceToken,
  refetchSurvey,
  defaultLanguage,
  surveyLanguages,
}) {
  const [currentStep, setCurrentStep] = useState(1)
  const {
    file: csvFile,
    loading: csvLoading,
    hasError,
    handleUpload,
    setFile: setCsvFile,
    setHasError,
  } = useCsvUpload()

  const [buttonLoading, setButtonLoading] = useState(false)
  const [targetLanguage, setTargetLanguage] = useState([])
  const [invalidLanguages, setInvalidLanguages] = useState([])
  const [importErrorMessage, setImportErrorMessage] = useState([])

  const { fetch: importLanguageTranslations } = useAxios({
    url: `survey/import-survey-languages/`,
    method: 'post',
    skip: true,
  })
  const { fetch: exportSurveyLanguages } = useAxios({
    url: `survey/${survey.token}/export-survey-languages/`,
    method: 'post',
    skip: true,
  })

  const downloadTemplate = () => {
    exportSurveyLanguages({
      data: { default_language: defaultLanguage, survey_languages: surveyLanguages },
    })
      .then((response) => {
        const csvData = response.data.rows.map((row) =>
          response.data.headers.reduce((obj, header, index) => {
            obj[header] = row[index]
            return obj
          }, {})
        )
        csvDownload(csvData, `${slugify(survey.name)}_${survey.id}_translations.csv`, null, {
          userInputFields: response.data.headers
        })
      })
      .catch(() => toast.error('Failed to download template,please contact support'))
  }

  const availableLanguageCodes = useMemo(
    () => Object.values(languageOption).map((code) => code.toLowerCase()),
    []
  )

  useEffect(() => {
    if (csvFile.data) {
      if (hasError) {
        toast.error('There was an error uploading the file, please try again')
        return
      }

      const languageCodes = csvFile.data[0].filter((value) => value !== 'key' && value !== 'field')
      setTargetLanguage(languageCodes)

      const invalidLanguageCodes = languageCodes.filter(
        (code) => !availableLanguageCodes.includes(code.toLowerCase())
      )
      const InvalidLanguagesError = invalidLanguageCodes.map((code) => ({
        language: code,
        error: 'Is not a supported language code',
      }))

      setInvalidLanguages(InvalidLanguagesError)
      validateSurveyLanguageCsvFile(
        csvFile.rawFile,
        csvFile.data[0],
        availableLanguageCodes,
        setInvalidLanguages
      )
      setCurrentStep(2)
    }
  }, [csvFile, hasError, availableLanguageCodes])

  async function handleStepChange(type) {
    const isNext = type === 'next'
    if (!isNext) {
      setCurrentStep(currentStep - 1)
      return
    }

    if (currentStep === 1) {
      setCurrentStep(2)
    } else if (currentStep === 2 && isNext) {
      setButtonLoading(true)
      const formData = new FormData()
      formData.append('file', csvFile.rawFile)
      const data = {
        survey_token: survey.token,
        workspace_token: workspaceToken,
        is_multilingual_survey_enabled: true,
      }
      formData.append('data', JSON.stringify(data))
      importLanguageTranslations({
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then(() => {
          toast.success('Languages updated successfully.')
          refetchSurvey()
          close()
        })
        .catch((error) => {
          setImportErrorMessage(
            error.response?.data['generic_errors'] || ['An unknown error occurred.']
          )
          setButtonLoading(false)
          setCurrentStep(3)
        })
    }
  }

  const renderStepHeader = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="flex flex-row items-center justify-between">
            Import translations
            <a
              href="https://help.simplesat.io/en/articles/2696158-translating-surveys-to-other-languages"
              target="_blank"
              rel="noreferrer"
              className="text-base font-normal"
            >
              <i className="icon far fa-book" /> How to import translations
            </a>
          </div>
        )
      case 2:
        return 'Review before importing'
      case 3:
        return 'Import failed'
      default:
        return ''
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <UploadTranslationPage
            setFile={setCsvFile}
            fileLoading={csvLoading}
            onUpload={handleUpload}
            setHasError={setHasError}
            file={csvFile}
            downloadTemplate={downloadTemplate}
          />
        )
      case 2:
        return (
          <TranslationReviewPage
            targetLanguage={targetLanguage}
            invalidLanguage={invalidLanguages}
          />
        )
      case 3:
        return <ImportFailedPage errorMessage={importErrorMessage} />
      default:
        return null
    }
  }
  const shouldDisplayBackButton = currentStep !== 1
  const shouldDisplayNextButton = currentStep !== 3 && csvFile.data
  return (
    <Modal>
      <Modal.Header>{renderStepHeader()}</Modal.Header>
      <Modal.Content scrolling>{renderStepContent()}</Modal.Content>
      {(shouldDisplayBackButton || shouldDisplayNextButton) && (
        <Modal.Actions>
          {shouldDisplayBackButton && (
            <Button onClick={() => handleStepChange('back')}>Back</Button>
          )}
          {shouldDisplayNextButton && (
            <Button primary onClick={() => handleStepChange('next')} loading={buttonLoading}>
              {currentStep == 2 ? 'Import' : 'Next'}
            </Button>
          )}
        </Modal.Actions>
      )}
    </Modal>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/LanguageImportModal/index.js